import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BuddyApiError } from '@nycelogic/nycebuddy-api-client';
import { useTranslation } from '@nycelogic/nycebuddy-chat-ui/i18n';
import { client, storeLogin } from '../auth/session';

type State =
  | { kind: 'pending' }
  | { kind: 'missing' }
  | { kind: 'expired' }
  | { kind: 'mismatch' }
  | { kind: 'error'; message: string };

/**
 * `/launch#code=…` — opened by the NYCE.LOGIC web client (START_BUDDY workflow: ticket → `POST /login/launch` →
 * one-time code). The code is redeemed once for tokens, never stored, and dropped from the history entry. The login
 * result goes to sessionStorage (`nb:` keys), so a launch in another tab is a separate session.
 */
export function Launch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ kind: 'pending' });

  useEffect(() => {
    const hash = new URLSearchParams(window.location.hash.slice(1));
    const code = hash.get('code');
    if (!code) {
      setState({ kind: 'missing' });
      return;
    }
    // `system` is optional: when the launcher names it, the server refuses a code minted for another installation.
    const system = hash.get('system') ?? undefined;
    let alive = true;
    client
      .loginRedeem({ code, system })
      .then((res) => {
        if (!alive) return;
        storeLogin(res);
        navigate(`/${encodeURIComponent(res.system)}`, { replace: true });
      })
      .catch((e: unknown) => {
        if (!alive) return;
        const status = e instanceof BuddyApiError ? e.status : 0;
        const failure = e instanceof BuddyApiError ? e.code : undefined;
        if (status === 403 || failure === 'system_mismatch') setState({ kind: 'mismatch' });
        else if (status === 410 || status === 404 || failure === 'code_expired')
          setState({ kind: 'expired' });
        else setState({ kind: 'error', message: (e as Error)?.message || t('launch.failed') });
      });
    return () => {
      alive = false;
    };
  }, [navigate, t]);

  const text =
    state.kind === 'pending'
      ? t('launch.signingIn')
      : state.kind === 'missing'
        ? t('launch.missingCode')
        : state.kind === 'expired'
          ? t('launch.expired')
          : state.kind === 'mismatch'
            ? t('launch.systemMismatch')
            : state.message;
  return (
    <div className="app">
      <div className="app-center" role="status">
        <p>{text}</p>
        {state.kind !== 'pending' && <Link to="/">{t('launch.goToSignIn')}</Link>}
      </div>
    </div>
  );
}
